import type { Me } from '@laborelec/lbe-react/auth';
import type { IDToken } from '@okta/okta-auth-js';
import axios from 'axios';
import oktaAuth from '../auth/oktaConfig';

const http = axios.create({
  baseURL: `${import.meta.env.VITE_BE_BASE_URL}technodb-api/`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// The backend authorizes on the `groups` claim, which the Okta Org
// authorization server only puts on the ID token - so the ID token is the
// Bearer credential. Renew near-expiry tokens so we don't ship stale ones and
// trigger 401 → redirect loops.
http.interceptors.request.use(async (config) => {
  try {
    let idToken = (await oktaAuth.tokenManager.get('idToken')) as IDToken | undefined;
    if (idToken && oktaAuth.tokenManager.hasExpired(idToken)) {
      idToken = (await oktaAuth.tokenManager.renew('idToken')) as IDToken | undefined;
    }
    if (idToken) {
      config.headers.Authorization = `Bearer ${idToken.idToken}`;
    }
  } catch {
    // Renewal failed - let the request go through unauthenticated; the
    // response interceptor will surface the 401.
  }
  return config;
});

// On 401 only redirect when the user genuinely has no session. Avoids
// bouncing the user out for an authorization-shaped 401 (e.g. backend
// rejected the token but we still have a valid Okta session about to renew).
http.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const authenticated = await oktaAuth.isAuthenticated();
      if (!authenticated) {
        oktaAuth.signInWithRedirect({
          originalUri: window.location.pathname + window.location.search,
        });
      }
    }
    return Promise.reject(error);
  },
);

interface UserInfo {
  user_id: string | null;
  email: string | null;
  scopes: string[];
  permissions: string[];
}

export interface CountResult {
  /** Rows visible to the current user. */
  count: number;
  /** Rows filtered out due to NDA gating (always 0 for unrestricted users). */
  hidden: number;
}

/** Metadata for a document attached to an entity - either an uploaded file or an external link. */
export interface DocumentMeta {
  id: number;
  entity_type: string;
  entity_id: number;
  /** Single-doc field name (use case A); ``null`` for the free-form section (use case B). */
  field_name: string | null;
  /** Display name. For files: original filename. For links: user label. */
  filename: string;
  content_type: string | null;
  size_bytes: number | null;
  /** Set iff this row is an uploaded file (mutually exclusive with ``external_url``). */
  s3_key: string | null;
  /** Set iff this row is an external link (mutually exclusive with ``s3_key``). */
  external_url: string | null;
  dt_created?: string | null;
  created_by?: string | null;
  dt_updated?: string | null;
  updated_by?: string | null;
}

/** True when the document is an external link rather than an uploaded file. */
export const isLinkDocument = (doc: DocumentMeta): boolean => !!doc.external_url;

interface ListDocumentsOpts {
  /** Filter to a specific single-doc field (use case A). */
  fieldName?: string;
  /** When true, only return free-form section documents (field_name IS NULL). */
  sectionOnly?: boolean;
}

/** Trigger a browser download for an opaque blob. */
export const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
};

export { http };

export const api = {
  list: async <T,>(path: string): Promise<T[]> => (await http.get<T[]>(path)).data,
  count: async (
    path: string,
    filter?: { field: string; value: number | string },
  ): Promise<CountResult> => {
    const params = filter ? { filter_field: filter.field, filter_value: filter.value } : undefined;
    const { data } = await http.get<CountResult>(`${path}/count`, { params });
    return data;
  },
  get: async <T,>(path: string, id: number | string): Promise<T> =>
    (await http.get<T>(`${path}/${id}`)).data,
  create: async <T,>(path: string, payload: Partial<T>): Promise<T> =>
    (await http.post<T>(path, payload)).data,
  update: async <T,>(path: string, id: number | string, payload: Partial<T>): Promise<T> =>
    (await http.patch<T>(`${path}/${id}`, payload)).data,
  put: async <T, B = unknown>(path: string, body: B): Promise<T> =>
    (await http.put<T>(path, body)).data,
  remove: async (path: string, id: number | string): Promise<void> => {
    await http.delete(`${path}/${id}`);
  },
  getMe: async (): Promise<UserInfo> => (await http.get<UserInfo>('me')).data,
  /** `/me` as lbe-react's auth expects it: the technodb permissions are the roles of the `technodb` scope. */
  getMeRoles: async (): Promise<Me> => {
    const me = await api.getMe();
    return { ...me, user_id: me.user_id ?? '', roles: { technodb: me.permissions } };
  },

  /** Document attachments stored in object storage (S3 / MinIO). */
  documents: {
    list: async (
      entityType: string,
      entityId: number,
      opts: ListDocumentsOpts = {},
    ): Promise<DocumentMeta[]> => {
      const params: Record<string, unknown> = { entity_type: entityType, entity_id: entityId };
      if (opts.fieldName !== undefined) params.field_name = opts.fieldName;
      if (opts.sectionOnly) params.section_only = true;
      const { data } = await http.get<DocumentMeta[]>('documents', { params });
      return data;
    },

    upload: async (
      entityType: string,
      entityId: number,
      file: File,
      fieldName?: string,
    ): Promise<DocumentMeta> => {
      const form = new FormData();
      form.append('entity_type', entityType);
      form.append('entity_id', String(entityId));
      if (fieldName) form.append('field_name', fieldName);
      form.append('file', file, file.name);
      const { data } = await http.post<DocumentMeta>('documents/upload', form, {
        // Let axios set the multipart boundary automatically.
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    /** Attach an external URL (SharePoint, Confluence, forum thread, ...) to an entity. */
    addLink: async (
      entityType: string,
      entityId: number,
      url: string,
      label?: string,
      fieldName?: string,
    ): Promise<DocumentMeta> => {
      const { data } = await http.post<DocumentMeta>('documents/link', {
        entity_type: entityType,
        entity_id: entityId,
        url,
        label: label || undefined,
        field_name: fieldName || undefined,
      });
      return data;
    },

    /** Open a document. Files stream through the API; links open in a new tab. */
    open: async (doc: DocumentMeta): Promise<void> => {
      if (doc.external_url) {
        // ``noopener,noreferrer`` so the target page can't reach back into our window.
        window.open(doc.external_url, '_blank', 'noopener,noreferrer');
        return;
      }
      const resp = await http.get<Blob>(`documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      saveBlob(resp.data, doc.filename);
    },

    remove: async (id: number): Promise<void> => {
      await http.delete(`documents/${id}`);
    },
  },
};
